import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { NEWS_ARTICLES, NEWS_CATEGORIES } from '@/mocks/news';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const article = NEWS_ARTICLES.find((a) => a.id === id);
  const related = NEWS_ARTICLES.filter((a) => a.id !== id && a.category === article?.category).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} | META66资讯`;

      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://meta66.cn';

      const schemas = [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          '@id': `${siteUrl}/news/${article.id}#article`,
          headline: article.title,
          description: article.summary,
          image: article.cover,
          url: `${siteUrl}/news/${article.id}`,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          inLanguage: 'zh-CN',
          author: {
            '@type': 'Person',
            name: article.author,
            image: article.authorAvatar,
          },
          publisher: {
            '@type': 'Organization',
            name: 'META66',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/logo.png`,
            },
          },
          keywords: article.tags.join(','),
          articleSection: article.category,
          isPartOf: { '@id': `${siteUrl}/news#webpage` },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: '资讯中心', item: `${siteUrl}/news` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `${siteUrl}/news/${article.id}` },
          ],
        },
      ];

      const existingScript = document.getElementById('schema-article-jsonld');
      if (existingScript) existingScript.remove();

      const script = document.createElement('script');
      script.id = 'schema-article-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemas);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('schema-article-jsonld');
      if (s) s.remove();
    };
  }, [id, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#020810] flex flex-col items-center justify-center gap-6">
        <Navbar />
        <div className="flex flex-col items-center gap-4 mt-32">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/5 border border-white/10">
            <i className="ri-file-unknow-line text-white/30 text-3xl" />
          </div>
          <p className="text-white/40 text-lg">文章不存在</p>
          <button onClick={() => navigate('/news')} className="px-6 py-3 rounded-full bg-[#00FF88] text-black font-bold text-sm hover:bg-[#00FFCC] transition-colors whitespace-nowrap cursor-pointer">
            返回资讯列表
          </button>
        </div>
      </div>
    );
  }

  const categoryLabel = NEWS_CATEGORIES.find((c) => c.id === article.category)?.label || article.category;

  return (
    <div className="min-h-screen bg-[#020810]" style={{ fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <Navbar />

      {/* Hero Cover */}
      <div className="relative h-[320px] md:h-[480px] overflow-hidden">
        <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020810] via-[#020810]/60 to-[#020810]/20" />
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-10 pb-10 max-w-[1000px] mx-auto w-full">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="px-3 py-1 rounded-full bg-[#00FF88]/20 border border-[#00FF88]/40 text-[#00FF88] text-xs font-semibold">{categoryLabel}</span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-white/10 text-white/50 text-xs">{tag}</span>
            ))}
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white leading-tight">{article.title}</h1>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-[1000px] mx-auto px-4 md:px-10 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-white/30 text-sm mb-8 flex-wrap">
          <Link to="/" className="hover:text-[#00FF88] transition-colors whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/news" className="hover:text-[#00FF88] transition-colors whitespace-nowrap">资讯中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/50 line-clamp-1">{article.title}</span>
        </div>

        {/* Meta Info */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-[#00FF88]/30">
              <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="text-white/80 text-sm font-semibold">{article.author}</p>
              <div className="flex items-center gap-3 text-white/30 text-xs">
                <span>{article.publishDate}</span>
                <span><i className="ri-time-line mr-1" />{article.readTime}分钟阅读</span>
                <span><i className="ri-eye-line mr-1" />{article.views.toLocaleString()}次阅读</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLiked(!liked)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 text-sm cursor-pointer whitespace-nowrap ${liked ? 'border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10' : 'border-white/10 text-white/40 hover:border-[#00FF88]/40 hover:text-[#00FF88]'}`}
            >
              <i className={liked ? 'ri-heart-fill' : 'ri-heart-line'} />
              <span>{liked ? '已点赞' : '点赞'}</span>
            </button>
            <button
              onClick={() => setBookmarked(!bookmarked)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 text-sm cursor-pointer whitespace-nowrap ${bookmarked ? 'border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10' : 'border-white/10 text-white/40 hover:border-[#00FF88]/40 hover:text-[#00FF88]'}`}
            >
              <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} />
              <span>{bookmarked ? '已收藏' : '收藏'}</span>
            </button>
            <button
              onClick={() => { if (navigator.share) { navigator.share({ title: article.title, url: window.location.href }); } }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-white/40 hover:border-[#00FF88]/40 hover:text-[#00FF88] transition-all duration-200 text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-line" />
              <span>分享</span>
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="p-5 rounded-xl bg-[#00FF88]/5 border border-[#00FF88]/20 mb-8">
          <p className="text-white/70 text-sm leading-relaxed">{article.summary}</p>
        </div>

        {/* Article Content */}
        <div className="prose-meta66">
          {article.content.split('\n\n').map((block, idx) => {
            if (block.startsWith('**') && block.endsWith('**')) {
              return (
                <h4 key={idx} className="text-[#00FF88] font-bold text-base mt-8 mb-3">
                  {block.replace(/\*\*/g, '')}
                </h4>
              );
            }
            if (block.startsWith('- ') || block.includes('\n- ')) {
              const items = block.split('\n').filter((l) => l.startsWith('- '));
              return (
                <ul key={idx} className="space-y-2 mb-6">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/60 text-sm leading-relaxed">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] mt-2 flex-shrink-0" />
                      <span>{item.replace('- ', '')}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            if (/^\d+\./.test(block) || block.includes('\n1.')) {
              const items = block.split('\n').filter((l) => /^\d+\./.test(l));
              return (
                <ol key={idx} className="space-y-2 mb-6">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/60 text-sm leading-relaxed">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#00FF88]/20 text-[#00FF88] text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{item.replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  ))}
                </ol>
              );
            }
            return (
              <p key={idx} className="text-white/60 text-sm leading-relaxed mb-5">
                {block}
              </p>
            );
          })}
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 flex-wrap mt-10 pt-8 border-t border-white/8">
          <span className="text-white/30 text-xs">标签：</span>
          {article.tags.map((tag) => (
            <Link
              key={tag}
              to={`/news?tag=${tag}`}
              className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/40 text-xs hover:border-[#00FF88]/40 hover:text-[#00FF88] transition-all duration-200"
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>

      {/* Related Articles */}
      {related.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 md:px-10 pb-16">
          <h4 className="text-white/60 text-sm font-semibold tracking-widest uppercase mb-6">相关资讯</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/news/${rel.id}`}
                className="group rounded-xl overflow-hidden border border-white/8 hover:border-[#00FF88]/40 transition-all duration-300 hover:-translate-y-1 bg-[#020810] block"
              >
                <div className="relative h-36 overflow-hidden">
                  <img src={rel.cover} alt={rel.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-4">
                  <p className="text-white/80 text-xs font-semibold leading-snug line-clamp-2 group-hover:text-[#00FF88] transition-colors duration-200">{rel.title}</p>
                  <p className="text-white/30 text-xs mt-2">{rel.publishDate}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="max-w-[1000px] mx-auto px-4 md:px-10 pb-10">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 text-white/50 text-sm hover:border-[#00FF88]/40 hover:text-[#00FF88] transition-all duration-200 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          <span>返回资讯列表</span>
        </Link>
      </div>

      <Footer />
    </div>
  );
}
